import { object } from '@art-suite/art-core-ts';
import { useMemo } from 'react';

import {
  getOrgPropertiesQueryKey,
  getOrgPropertiesQueryKeyPrefix,
  getOrgPropertyQueryKey,
  getOrgPropertyQueryKeyPrefix,
  KaiProApiClient,
} from '@kaiizen/kai-pro-api';
import { useCachedQuery, useQueryCache, useQueryCacheMutation } from '@kaiizen/react-persistent-cache';

import { ScanOfflineDataMinTtlOnAccessMs } from '../apiScanAndTemplateState/ScanStateLib';
import { useCreateUpdateMutatorsWithQueryCache } from '../reactQueryLib';
import { useKaiProApiClient } from '../useKaiProApiClient';

const getPropertyMutators = (kaiProApiClient: KaiProApiClient) => {
  const queryCache = useQueryCache();
  return {
    ...useCreateUpdateMutatorsWithQueryCache({
      apiCreate: kaiProApiClient.vivifyOrgProperty.bind(kaiProApiClient),
      apiUpdate: kaiProApiClient.updateOrgProperty.bind(kaiProApiClient),
      pluralKeyPrefix: getOrgPropertiesQueryKeyPrefix(),
      getSingleKey: getOrgPropertyQueryKey,
    }),
    refetchAllProperties: () => queryCache.refetchAll({ prefix: getOrgPropertyQueryKeyPrefix() }),
  };
};

export type OrgPropertyMutators = ReturnType<typeof getPropertyMutators>;

export const useOrgProperties = (options?: { archived?: boolean }) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    getOrgPropertiesQueryKey(options?.archived),
    async (key, queryCache) => {
      const properties = await kaiProApiClient.getOrgProperties({ archived: options?.archived ? true : undefined });
      properties.forEach(property => {
        queryCache.cache.set(getOrgPropertyQueryKey(property.id), property);
      });
      return properties;
    },
    { enabled: !!kaiProApiClient.token },
  );

  const properties = query.data;
  const propertiesById = useMemo(() => properties ? object(properties, { withKey: ({ id }) => id }) : undefined, [properties]);

  return {
    ...query,
    properties,
    propertiesById,
    propertiesMutators: getPropertyMutators(kaiProApiClient),
  };
};

export const useOrgProperty = (
  orgPropertyId?: string,
) => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    getOrgPropertyQueryKey(orgPropertyId),
    async (key, queryCache) => {
      const property = await kaiProApiClient.getOrgProperty(orgPropertyId!);
      queryCache.cache.set(getOrgPropertyQueryKey(property.id), property);
      return property;
    },
    { enabled: !!kaiProApiClient.token && !!orgPropertyId, minTtlOnAccessMs: ScanOfflineDataMinTtlOnAccessMs },
  );

  return {
    ...query,
    orgProperty: query.data,
    propertyMutators: getPropertyMutators(kaiProApiClient),
  };
};

/**
 * Super admin only: uploads a CSV of properties (header row orgId, address, customId, market) and returns each row's result.
 * Never retried: an upload that timed out may still be running on the server.
 */
export const useBatchUploadOrgProperties = () => {
  const kaiProApiClient = useKaiProApiClient();
  const queryCache = useQueryCache();
  return useQueryCacheMutation(
    (file: File) => kaiProApiClient.batchUploadOrgProperties(file),
    {
      maxRetryMs: 0,
      onSuccess: () => queryCache.refetchAll({ prefix: getOrgPropertyQueryKeyPrefix() }),
    },
  );
};
